import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  noIndex?: boolean;
}

const SEO = ({ title, description, keywords, canonical, noIndex = false }: SEOProps) => {
  const location = useLocation();
  const siteName = "LVK Haldus";
  const siteUrl = "https://www.lvkhaldus.ee";
  const fullTitle = `${title} | ${siteName}`;
  const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;

  // Build breadcrumb from path
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const breadcrumbItems = [
    { "@type": "ListItem", position: 1, name: "Avaleht", item: siteUrl },
    ...pathSegments.map((seg, i) => ({
      "@type": "ListItem",
      position: i + 2,
      name: seg.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      item: `${siteUrl}/${pathSegments.slice(0, i + 1).join("/")}`,
    })),
  ];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="author" content="LVK Haldus OÜ" />
      <meta name="geo.region" content="EE" />
      <meta name="geo.placename" content="Eesti" />
      <link rel="alternate" hrefLang="et" href={canonicalUrl} />

      {/* Preconnect for performance */}
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="et_EE" />
      <meta property="og:image" content={`${siteUrl}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}/og-image.png`} />

      {/* Schema.org - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "LVK Haldus OÜ",
          "description": "Professionaalsed koristusteenused ja kinnisvara haldusteenus üle Eesti.",
          "url": siteUrl,
          "telephone": "+372 5385 5694",
          "email": "info@lvkhaldus.ee",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "EE"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Eesti"
          },
          "priceRange": "€€",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          },
          "sameAs": [],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Koristusteenused",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Hoolduskoristus" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Eripuhastustööd" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Haldusteenused" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Väliskoristused" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Haljastus ja muru niitmine" } }
            ]
          }
        })}
      </script>

      {/* Schema.org - BreadcrumbList */}
      {pathSegments.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbItems
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
